import React, { useState } from "react";
import styled from "styled-components";
import Loader from "./Loader";
import { zIndex } from "../config/zIndex";

// Styled Components
const Container = styled.div`
	background-color: white;
	border-radius: 0.5rem;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
	padding: 1.5rem;
	max-width: 20rem;
	position: relative;
`;

const Overlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(255, 255, 255, 0.7);
	border-radius: 0.5rem;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: ${zIndex.base};
`;

const Content = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
`;

const Title = styled.h3`
	font-size: 1.125rem;
	font-weight: 500;
	margin: 0;
`;

const ToggleButton = styled.button`
	position: relative;
	display: inline-flex;
	height: 1.5rem;
	width: 2.75rem;
	align-items: center;
	border-radius: 9999px;
	transition: background-color 0.3s;
	background-color: ${(props) => (props.isOn ? "rgba(45, 35, 66, 0.8)" : "#e5e7eb")};
	opacity: ${(props) => (props.isLoading ? 0.5 : 1)};
	cursor: ${(props) => (props.isLoading ? "not-allowed" : "pointer")};
	outline: none;
	border: none;

	&:focus {
		outline: none;
	}
`;

const ToggleKnob = styled.span`
	display: inline-block;
	height: 1rem;
	width: 1rem;
	background-color: white;
	border-radius: 9999px;
	transform: translateX(${(props) => (props.isOn ? "1.5rem" : "0.25rem")});
	transition: transform 0.3s;
`;

const StatusText = styled.div`
	font-size: 0.875rem;
	color: #6b7280;
`;

const Spinner = styled.svg`
	position: absolute;
	inset: 0;
	margin: auto;
	height: 1rem;
	width: 1rem;
	animation: spin 1s linear infinite;

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
`;

// Toggle Switch Component
const ToggleSwitch = ({ title, initialStatus = false, onChange, isAsync = false }) => {
	const [isOn, setIsOn] = useState(initialStatus);
	const [isLoading, setIsLoading] = useState(false);

	const handleToggle = async () => {
		if (isLoading) return;

		const newStatus = !isOn;
		setIsLoading(true);

		try {
			let result = false;

			if (isAsync) {
				result = await onChange(newStatus);
			} else {
				result = onChange(newStatus);
			}

			if (result) {
				setIsOn(newStatus);
			}
		} catch (error) {
			console.error("Error updating status:", error);
			// If there's an error, we don't update the state
		} 

        setIsLoading(false);
	};

	return (
		<Container>
			<Content>
				<Title>{title}</Title>
				<ToggleButton
					onClick={handleToggle}
					disabled={isLoading}
					isOn={isOn}
					isLoading={isLoading}
					aria-pressed={isOn}
					aria-label={`Toggle ${title}`}>
					<ToggleKnob isOn={isOn} />
				</ToggleButton>
				<StatusText>Current status: {isOn ? "On" : "Off"}</StatusText>
			</Content>
			{isLoading && (
				<Overlay />
			)}
		</Container>
	);
};

export default ToggleSwitch;
